import { getLogger } from "@expert/logging";
import { FullStory, init } from "@fullstory/browser";

const logger = getLogger({
    module: "fullstory",
});

interface FullStoryConfig {
    orgId: string;
    isDevMode: boolean;
    commitHash?: string;
    appVersion?: string;
    readyCallback?: Parameters<typeof init>[1];
}

let devMode = false;
let commitHash: string | undefined;
let appVersion: string | undefined;

export const initFullStory = ({
    orgId,
    isDevMode,
    commitHash: hash,
    appVersion: version,
    readyCallback,
}: FullStoryConfig) => {
    devMode = isDevMode;
    commitHash = hash;
    appVersion = version;

    try {
        init(
            {
                orgId,
                debug: true,
                devMode: isDevMode,
            },
            ({ sessionUrl, ...rest }) => {
                logger.debug({ sessionUrl }, "FullStory | Session initialized");
                readyCallback?.({ sessionUrl, ...rest });
            },
        );
    } catch (error) {
        logger.warn({ error }, "FullStory | Error initializing");
    }
};

interface FullStoryIdentifier {
    identity: string;
    displayName: string;
    properties?: object;
}

export const setFullStoryIdentifier = ({ identity, displayName, properties }: FullStoryIdentifier) => {
    if (!identity) return;
    if (devMode) return;

    try {
        const filteredProperties = Object.fromEntries(
            Object.entries(properties ?? {}).filter(([_, value]) => value !== null && value !== undefined),
        );
        FullStory("setIdentity", {
            uid: identity,
            properties: {
                displayName,
                ...filteredProperties,
                ...(commitHash ? { commitHash } : {}),
                ...(appVersion ? { appVersion } : {}),
            },
        });
    } catch (error) {
        logger.warn({ error }, "FullStory | Error setting identity");
    }
};

export const trackFullStoryEvent = (name: string, properties: object) => {
    if (devMode) return;

    try {
        FullStory("trackEvent", {
            name,
            properties,
        });
    } catch (error) {
        logger.warn({ error }, "FullStory | Error tracking event");
    }
};

export const getFullStoryUrl = async () => {
    if (devMode) return;

    try {
        return await FullStory("getSessionAsync", { format: "url.now" });
    } catch (error) {
        logger.warn({ error }, "FullStory | Error getting session URL");
        return undefined;
    }
};
